import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";

// Same schedule model as the doctor app (Edit profile → Consultation schedule):
// working days, 1–4 time blocks that apply to every working day, slot length, break.
export const DAYS = [
  ["monday", "Mon", "Monday"], ["tuesday", "Tue", "Tuesday"], ["wednesday", "Wed", "Wednesday"],
  ["thursday", "Thu", "Thursday"], ["friday", "Fri", "Friday"], ["saturday", "Sat", "Saturday"], ["sunday", "Sun", "Sunday"],
];
const LEGACY = { mon: "monday", tue: "tuesday", wed: "wednesday", thu: "thursday", fri: "friday", sat: "saturday", sun: "sunday" };
const SLOT_LENGTHS = [15, 20, 30];
const BREAKS = [0, 5, 10];
const MAX_BLOCKS = 4;
const DEFAULT_BLOCKS = [{ startTime: "09:00", endTime: "13:00" }, { startTime: "17:00", endTime: "20:00" }];

const toMin = (hhmm) => {
  const [h, m] = (hhmm || "0:0").split(":");
  return (parseInt(h, 10) || 0) * 60 + (parseInt(m, 10) || 0);
};
const fromMin = (m) => `${String(Math.floor(m / 60) % 24).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

export function normalizeTimeSlots(raw) {
  const out = {};
  DAYS.forEach(([k]) => { out[k] = { enabled: false, startTime: "09:00", endTime: "17:00" }; });
  if (raw && typeof raw === "object") {
    Object.entries(raw).forEach(([k, v]) => {
      const key = out[k] ? k : LEGACY[k];
      if (key && v) out[key] = { ...out[key], ...v };
    });
  }
  return out;
}

const blocksProblem = (blocks, slotLen) => {
  for (const b of blocks) {
    if (toMin(b.endTime) <= toMin(b.startTime)) return "Each block must end after it starts.";
    if (toMin(b.endTime) - toMin(b.startTime) < slotLen) return `A block is shorter than one ${slotLen}-minute slot.`;
  }
  const sorted = [...blocks].sort((a, b) => toMin(a.startTime) - toMin(b.startTime));
  for (let i = 1; i < sorted.length; i++) {
    if (toMin(sorted[i].startTime) < toMin(sorted[i - 1].endTime)) return "Time blocks can't overlap.";
  }
  return null;
};

const slotsIn = (blocks, slotLen, breakLen) =>
  blocks.reduce((n, b) => {
    const span = toMin(b.endTime) - toMin(b.startTime);
    return span >= slotLen ? n + Math.floor((span + breakLen) / (slotLen + breakLen)) : n;
  }, 0);

const timeInput = { height: 36, borderRadius: 8, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--muted))", color: "hsl(var(--foreground))", padding: "0 10px", fontSize: 13, outline: "none" };

/** Dialog to edit a doctor's schedule. onSave(patch) must return a promise. */
const ScheduleEditor = ({ open, onOpenChange, doctor, onSave }) => {
  const [days, setDays] = useState(new Set());
  const [blocks, setBlocks] = useState(DEFAULT_BLOCKS);
  const [slotLen, setSlotLen] = useState(15);
  const [breakLen, setBreakLen] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !doctor) return;
    setError("");
    const saved = normalizeTimeSlots(doctor.timeSlots);
    const on = DAYS.map(([k]) => k).filter((k) => saved[k].enabled);
    if (on.length) {
      setDays(new Set(on));
      const first = saved[on[0]];
      setBlocks([{ startTime: first.startTime, endTime: first.endTime }, ...(first.extraBlocks || [])]);
    } else {
      setDays(new Set(["monday", "tuesday", "wednesday", "thursday", "friday", "saturday"]));
      setBlocks(DEFAULT_BLOCKS);
    }
    setSlotLen(Number(doctor.slotDuration) > 0 ? Number(doctor.slotDuration) : 15);
    setBreakLen(Number(doctor.breakBetweenSlots ?? doctor.bufferTime) || 0);
  }, [open, doctor]);

  const slotOptions = SLOT_LENGTHS.includes(slotLen) ? SLOT_LENGTHS : [...SLOT_LENGTHS, slotLen].sort((a, b) => a - b);
  const breakOptions = BREAKS.includes(breakLen) ? BREAKS : [...BREAKS, breakLen].sort((a, b) => a - b);
  const perDay = useMemo(() => slotsIn(blocks, slotLen, breakLen), [blocks, slotLen, breakLen]);

  const setBlock = (i, patch) => setBlocks((p) => p.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const addBlock = () =>
    setBlocks((p) => {
      const last = p[p.length - 1];
      const start = last ? Math.min(toMin(last.endTime) + 60, 22 * 60) : 9 * 60;
      return [...p, { startTime: fromMin(start), endTime: fromMin(Math.min(start + 120, 23 * 60 + 55)) }];
    });
  const toggleDay = (k) => setDays((p) => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const save = async () => {
    setError("");
    if (days.size === 0) return setError("Choose at least one working day.");
    if (blocks.length === 0) return setError("Add at least one time block.");
    const problem = blocksProblem(blocks, slotLen);
    if (problem) return setError(problem);
    const sorted = [...blocks].sort((a, b) => toMin(a.startTime) - toMin(b.startTime));
    const [first, ...rest] = sorted;
    const timeSlots = {};
    DAYS.forEach(([k]) => {
      timeSlots[k] = { enabled: days.has(k), startTime: first.startTime, endTime: first.endTime, ...(rest.length ? { extraBlocks: rest } : {}) };
    });
    setSaving(true);
    try {
      await onSave({ timeSlots, slotDuration: slotLen, breakBetweenSlots: breakLen });
      onOpenChange(false);
    } catch (e) {
      setError(e?.message || "Couldn't save the schedule");
    } finally {
      setSaving(false);
    }
  };

  const chip = (on) => ({
    height: 36, minWidth: 52, padding: "0 12px", borderRadius: 999, fontSize: 12.5, fontWeight: 600, cursor: "pointer", transition: "all .15s",
    border: `1.5px solid ${on ? "hsl(var(--primary))" : "hsl(var(--input))"}`,
    background: on ? "hsl(var(--primary))" : "hsl(var(--card))", color: on ? "hsl(var(--primary-foreground))" : "hsl(var(--muted-foreground))",
  });
  const label = { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "hsl(var(--muted-foreground))", margin: "0 0 8px", display: "block" };

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent style={{ maxWidth: 520, maxHeight: "90vh", overflowY: "auto" }}>
        <DialogHeader>
          <DialogTitle>Edit schedule</DialogTitle>
          <DialogDescription>Working days and consultation hours, the same as in the doctor app's settings.</DialogDescription>
        </DialogHeader>

        <div>
          <span style={label}>Working days</span>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {DAYS.map(([k, short, long]) => (
              <button key={k} type="button" aria-pressed={days.has(k)} aria-label={long} onClick={() => toggleDay(k)} style={chip(days.has(k))}>{short}</button>
            ))}
          </div>
        </div>

        <div>
          <span style={label}>Consultation hours (every working day)</span>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {blocks.map((b, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="time" className="num" value={b.startTime} onChange={(e) => setBlock(i, { startTime: e.target.value })} style={timeInput} />
                <span style={{ color: "hsl(var(--muted-foreground))" }}>to</span>
                <input type="time" className="num" value={b.endTime} onChange={(e) => setBlock(i, { endTime: e.target.value })} style={timeInput} />
                {blocks.length > 1 && (
                  <button type="button" aria-label="Remove block" onClick={() => setBlocks((p) => p.filter((_, j) => j !== i))} style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid hsl(var(--border))", background: "transparent", color: "hsl(var(--muted-foreground))", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
          {blocks.length < MAX_BLOCKS && (
            <button type="button" onClick={addBlock} style={{ marginTop: 10, display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: "hsl(var(--primary))", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
              <Plus size={14} /> Add another time block
            </button>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div>
            <span style={label}>Slot length</span>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {slotOptions.map((m) => <button key={m} type="button" onClick={() => setSlotLen(m)} style={chip(slotLen === m)} className="num">{m} min</button>)}
            </div>
          </div>
          <div>
            <span style={label}>Break between slots</span>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {breakOptions.map((m) => <button key={m} type="button" onClick={() => setBreakLen(m)} style={chip(breakLen === m)} className="num">{m ? `${m} min` : "None"}</button>)}
            </div>
          </div>
        </div>

        <div style={{ padding: "10px 12px", borderRadius: 10, background: "hsl(var(--tint-blue))", border: "1px solid hsl(var(--tint-blue-border))", color: "hsl(var(--on-blue))", fontSize: 12.5 }}>
          <b className="num">{perDay}</b> slots a day · <b className="num">{perDay * days.size}</b> a week
        </div>

        {error && <p role="alert" style={{ margin: 0, fontSize: 12.5, color: "hsl(var(--on-red))" }}>{error}</p>}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" disabled={saving} onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={saving} onClick={save}>
            {saving && <Loader2 size={14} className="animate-spin" />} Save schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleEditor;
